import React, { useState, useMemo, useRef } from 'react';
import { Icons as I } from '../pack-icons';

/**
 * EngagementChart — interactive SVG chart of engagement % over time.
 * data: [{ label, engagement, published }]  (chronological)
 * partial: true → renders "Partial data" hint
 * onRangeChange: optional callback when user clicks a range button
 *
 * If data is empty → renders empty state card.
 */
export default function EngagementChart({ data = [], height = 220, partial = false, onRangeChange }) {
  const [range, setRange] = useState('30d');
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const slice = useMemo(() => {
    const map = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 };
    const n = map[range] || 30;
    return data.slice(-n);
  }, [data, range]);

  if (!data?.length) {
    return (
      <div className="chart-card card">
        <div className="chart-head">
          <div>
            <div className="eyebrow"><span>Engagement</span></div>
            <h3 className="chart-title">No analytics yet</h3>
            <div className="chart-partial">
              <I.info size={12} />
              <span>Analytics data appears here once posts start being published.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const W = 760;
  const H = height;
  const padX = 28;
  const padY = 22;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;

  const maxYRaw = Math.max(...slice.map((d) => Math.max(d.engagement || 0, d.published || 0)));
  const maxY = maxYRaw > 0 ? maxYRaw * 1.2 : 5;
  const minY = 0;
  const xStep = innerW / Math.max(1, slice.length - 1);

  const xy = (i, v) => [padX + i * xStep, padY + innerH - ((v - minY) / (maxY - minY)) * innerH];

  const buildPath = (key) => {
    if (slice.length < 2) return '';
    const pts = slice.map((d, i) => xy(i, d[key] || 0));
    let p = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const [x0, y0] = pts[Math.max(0, i - 1)];
      const [x1, y1] = pts[i];
      const [x2, y2] = pts[i + 1];
      const [x3, y3] = pts[Math.min(pts.length - 1, i + 2)];
      const cp1x = x1 + (x2 - x0) / 6;
      const cp1y = y1 + (y2 - y0) / 6;
      const cp2x = x2 - (x3 - x1) / 6;
      const cp2y = y2 - (y3 - y1) / 6;
      p += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
    }
    return p;
  };

  const engagementPath = buildPath('engagement');
  const publishedPath = buildPath('published');
  const areaPath = engagementPath
    ? engagementPath + ` L ${padX + (slice.length - 1) * xStep} ${padY + innerH} L ${padX} ${padY + innerH} Z`
    : '';

  const onMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W - padX;
    const i = Math.max(0, Math.min(slice.length - 1, Math.round(x / xStep)));
    setHover(i);
  };
  const onLeave = () => setHover(null);

  const xLabels = slice.length > 14
    ? [0, Math.round((slice.length - 1) * 0.5), slice.length - 1]
    : slice.length > 7
      ? [0, Math.round((slice.length - 1) * 0.33), Math.round((slice.length - 1) * 0.66), slice.length - 1]
      : slice.map((_, i) => i);
  const yLabels = 4;

  const totalEngagement = slice.reduce((s, d) => s + (d.engagement || 0), 0);
  const totalPublished = slice.reduce((s, d) => s + (d.published || 0), 0);
  const avgEngagement = slice.length > 0 ? (totalEngagement / slice.length).toFixed(1) : '0.0';
  const delta = slice.length > 1 ? ((slice[slice.length - 1].engagement || 0) - (slice[0].engagement || 0)) : 0;

  return (
    <div className="chart-card card">
      <div className="chart-head">
        <div>
          <div className="eyebrow"><span>Engagement</span></div>
          <h3 className="chart-title">Last {slice.length} days</h3>
          {partial && (
            <div className="chart-partial">
              <I.info size={12} />
              <span>Partial data — analytics worker is filling in.</span>
            </div>
          )}
        </div>
        <div className="chart-ranges">
          {['7d', '14d', '30d', '90d'].map((r) => (
            <button
              key={r}
              className={r === range ? 'is-active' : ''}
              onClick={() => { setRange(r); onRangeChange?.(r); }}
              type="button"
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="chart-stats">
        <div>
          <div className="cs-l">Avg engagement</div>
          <div className="cs-v">
            {avgEngagement}
            <span className="cs-unit">%</span>
            {delta !== 0 && (
              <span className={`cs-delta ${delta > 0 ? 'up' : 'down'}`}>
                {delta > 0 ? <I.arrowUpR size={11} /> : <I.arrowDownR size={11} />}
                {Math.abs(delta).toFixed(1)}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="cs-l">Posts published</div>
          <div className="cs-v">
            {totalPublished}
            <span className="cs-unit"> posts</span>
          </div>
        </div>
        <div className="cs-legend">
          <span className="cs-leg"><i style={{ background: 'var(--accent)' }} />Engagement %</span>
          <span className="cs-leg"><i style={{ background: 'var(--text-subtle)', opacity: 0.6 }} />Posts published</span>
        </div>
      </div>

      <div className="chart-svg-wrap" onMouseMove={onMove} onMouseLeave={onLeave}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="chart-svg">
          <defs>
            <linearGradient id="engGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.20" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="engStroke" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="var(--grad-cyan)" />
              <stop offset="50%" stopColor="var(--grad-purple)" />
              <stop offset="100%" stopColor="var(--grad-magenta)" />
            </linearGradient>
          </defs>

          {Array.from({ length: yLabels + 1 }).map((_, i) => {
            const y = padY + (i / yLabels) * innerH;
            const v = maxY - (i / yLabels) * (maxY - minY);
            return (
              <g key={i}>
                <line
                  x1={padX} x2={W - padX} y1={y} y2={y}
                  stroke="var(--border-subtle)" strokeWidth="1"
                  strokeDasharray={i === yLabels ? '0' : '2 3'}
                />
                <text
                  x={padX - 8} y={y + 3}
                  textAnchor="end" fontSize="9.5"
                  fill="var(--text-subtle)" fontFamily="var(--font-mono)"
                >
                  {v.toFixed(maxY < 10 ? 1 : 0)}
                </text>
              </g>
            );
          })}

          {areaPath && <path d={areaPath} fill="url(#engGrad)" />}

          {publishedPath && (
            <path d={publishedPath} fill="none" stroke="var(--text-subtle)" strokeWidth="1.4" strokeDasharray="4 4" opacity="0.55" />
          )}

          {engagementPath && (
            <path d={engagementPath} fill="none" stroke="url(#engStroke)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          )}

          {hover != null && slice[hover] && (() => {
            const [hx, hy] = xy(hover, slice[hover].engagement || 0);
            const [px, py] = xy(hover, slice[hover].published || 0);
            return (
              <g>
                <line x1={hx} x2={hx} y1={padY} y2={padY + innerH} stroke="var(--accent)" strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
                <circle cx={px} cy={py} r="3" fill="var(--bg-elev)" stroke="var(--text-subtle)" strokeWidth="1.4" />
                <circle cx={hx} cy={hy} r="4.5" fill="var(--bg-elev)" stroke="var(--accent)" strokeWidth="2" />
              </g>
            );
          })()}

          {xLabels.map((i, k) => {
            const d = slice[i];
            const [x] = xy(i, 0);
            return (
              <text key={k} x={x} y={H - 4} textAnchor="middle" fontSize="9.5" fill="var(--text-subtle)" fontFamily="var(--font-mono)">
                {d?.label || ''}
              </text>
            );
          })}
        </svg>

        {hover != null && slice[hover] && (() => {
          const [hx] = xy(hover, 0);
          const left = (hx / W) * 100;
          const flip = left > 70;
          return (
            <div className={`chart-tip ${flip ? 'flip' : ''}`} style={{ left: left + '%' }}>
              <div className="chart-tip-l">{slice[hover].label}</div>
              <div className="chart-tip-row">
                <i style={{ background: 'var(--accent)' }} />
                <span>Engagement</span>
                <b className="mono">{(slice[hover].engagement || 0).toFixed(1)}%</b>
              </div>
              <div className="chart-tip-row">
                <i style={{ background: 'var(--text-subtle)', opacity: 0.6 }} />
                <span>Published</span>
                <b className="mono">{slice[hover].published || 0}</b>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
